import Button from "components/library/Button";
import { classNames } from "components/library/utils/classNames";
import { Fragment } from "react";
import { CheckIcon, MinusIcon } from "@heroicons/react/outline";
import { SectionBadge } from "@components/SectionBadge";
import {
  AnnotateIcon,
  CheckCircleIcon,
  ShapeCubeIcon,
} from "@components/library/Icons";
import {
  ActiveLearningLogo,
  ActiveTestingLogo,
  AnnotationTeamsLogo,
  AutoTrainLogo,
  DataDebuggerLogo,
  HumanInTheLoopDeploymentLogo,
  InferenceLogo,
  MonitoringLogo,
  ProgrammaticLogo,
} from "@components/ProductsLogos";
import { HUMANLOOP_APP_SIGNUP, HUMANLOOP_TYPEFORM } from "utils/constants";
import Tooltip from "@components/library/Tooltip";
import { ConditionalWrapper } from "@components/library/utils/wrap";

const AIBadge = () => (
  <span>
    <div className="mx-2 inline-flex items-center justify-center rounded-ms  py-1 px-4  text-11-14 font-bold">
      AI
    </div>
  </span>
);

export type Tier = {
  name: string;
  price: string;
  pricePer?: React.ReactNode;
  minimumUsers?: 5;
  offer?: string;
  description: string;
  buttonText: string;
  href: string;
  features: string[];
  highlighted?: boolean;
};

export const tiers: Tier[] = [
  {
    name: "Individual",
    href: HUMANLOOP_TYPEFORM,
    price: "$0",
    pricePer: " / mo",
    description: "For individuals who want to get annotated data",
    buttonText: "Sign up",
    features: [
      "Programmatic labeling",
      "Best-in-class annotation tooling",
      "Export annotated data",
    ],
  },
  {
    name: "Company",
    href: HUMANLOOP_TYPEFORM,
    price: "$49",
    pricePer: "per user / mo",
    minimumUsers: 5,
    description:
      "For teams that are looking to get models to production fast and efficiently.",
    offer: "Free to try for 14 days",

    highlighted: true,
    buttonText: "Start free trial",
    features: [
      "Everything in the Individual plan, plus...",
      "5+ collaborators",
      "Model training",
      "Active learning",
      "Inference API",
    ],
  },
  {
    name: "Enterprise",
    href: HUMANLOOP_TYPEFORM,
    price: "Custom",
    description:
      "Tooling for large teams, mission critical deployments and model improvement.",
    buttonText: "Talk to sales",
    features: [
      "Unlimited collaborators",
      "Model improvement tools",
      "Self-hosted inference",
      "Dedicated ML support",
      "Private cloud",
    ],
  },
];

type Section = {
  name: string;
  features: {
    name: React.ReactNode;
    tiers: Record<string, boolean | string>;
    tooltip?: string;
  }[];
  displayTitle?: React.ReactNode;
};

export const sections: Section[] = [
  {
    name: "Overview",
    displayTitle: null,
    features: [
      {
        name: "Collaborators",
        tiers: {
          Individual: "Individual",
          Company: "5 seats minimum",
          Enterprise: "Unlimited",
        },
      },
    ],
  },
  {
    name: "Annotate",
    displayTitle: (
      <div className="flex items-center gap-8">
        <AnnotateIcon />
        <div className="">Annotate</div>
      </div>
    ),
    features: [
      {
        name: <ProgrammaticLogo />,
        tiers: { Individual: true, Company: true, Enterprise: true },
      },
      {
        name: <AnnotationTeamsLogo />,
        tiers: { Individual: true, Company: true, Enterprise: true },
      },
      {
        name: (
          <div className="flex gap-4">
            <ActiveLearningLogo />
            <AIBadge />
          </div>
        ),
        tiers: { Company: true, Enterprise: true },
      },
    ],
  },
  {
    name: "Deploy",
    displayTitle: (
      <div className="flex items-center gap-12">
        <ShapeCubeIcon />
        <div className="">Deploy</div>
      </div>
    ),
    features: [
      {
        name: (
          <div className="flex gap-4">
            <AutoTrainLogo />
            <AIBadge />
          </div>
        ),
        tiers: { Company: true, Enterprise: true },
      },
      {
        name: (
          <div className="flex gap-4">
            <InferenceLogo />
            <AIBadge />
          </div>
        ),
        tiers: { Company: true, Enterprise: true },
      },
      {
        name: <HumanInTheLoopDeploymentLogo />,
        tiers: { Company: true, Enterprise: true },
      },
    ],
  },
  {
    name: "Improve",
    displayTitle: (
      <div className="flex items-center gap-12">
        <CheckCircleIcon />
        <div className="">Improve</div>
      </div>
    ),
    features: [
      {
        name: (
          <div className="flex gap-4">
            <ActiveTestingLogo />
          </div>
        ),
        tiers: { Individual: false, Company: true, Enterprise: true },
      },
      {
        name: (
          <div className="flex gap-4">
            <DataDebuggerLogo />
          </div>
        ),
        tiers: { Company: false, Enterprise: true },
      },
      {
        name: (
          <div className="flex gap-4">
            <MonitoringLogo />
          </div>
        ),
        tiers: { Company: false, Enterprise: true },
      },
    ],
  },
  {
    name: "Other",
    displayTitle: null,
    features: [
      {
        name: (
          <>
            Price for <AIBadge /> features
          </>
        ),
        tooltip: "AI powered features scale with your data volumes",

        tiers: {
          Individual: false,
          Company: "* $0.0060/ 1k character",
          Enterprise: "* Volume based pricing",
        },
      },
    ],
  },
  {
    name: "Support",
    displayTitle: (
      <div className="flex items-center gap-12">
        <div className="">Support</div>
      </div>
    ),
    features: [
      {
        name: "API / SDK",
        tiers: { Individual: true, Company: true, Enterprise: true },
      },
      {
        name: "Single Sign-On (SSO)",
        tiers: { Individual: false, Company: false, Enterprise: true },
      },
      {
        name: "VPC Deployments",
        tiers: { Enterprise: true },
      },
      {
        name: "Enterprise SLA",
        tiers: { Enterprise: true },
      },
      {
        name: "Machine Learning Services",
        tiers: { Enterprise: true },
      },
      {
        name: "Support",
        tiers: {
          Individual: "Public Slack",
          Company: "Dedicated chat support (add-on)",
          Enterprise: true,
        },
      },
    ],
  },
];

export const PricingTable = () => {
  return (
    <div className="container-xl my-120" id="pricing-table">
      <h2 className="mb-32 font-display text-28-32">Compare all features</h2>
      <div className="mx-auto bg-white py-16 sm:py-24 ">
        {/* xs to md */}
        <div className="mx-auto max-w-2xl space-y-40 md:hidden">
          {tiers.map((tier, tierIdx) => (
            <section key={tier.name}>
              <div className="mb-8 ">
                <h2 className="text-18-24 font-bold text-gray-900">
                  {tier.name}
                </h2>
                <div className="mt-4 flex items-end gap-8">
                  <span className="pt-20 font-display text-18-24 font-bold sm:text-24-32">
                    {tier.price}
                  </span>
                  {tier.pricePer && (
                    <span className="text-14-20 sm:text-18-28">
                      {tier.pricePer}
                    </span>
                  )}
                </div>
                <p className="mt-16 text-14-16 text-gray-800">
                  {tier.description}
                </p>
                <Button
                  href={tier.href}
                  className="mt-36 block w-full border border-gray-800 bg-gray-800 py-2 text-center text-14-16 font-bold !text-white hover:bg-gray-900"
                >
                  {tier.buttonText}
                </Button>
              </div>

              {sections.map((section) => (
                <table key={section.name} className="w-full">
                  <caption className="border-t border-gray-300 bg-gray-200 py-12 px-16 text-left text-14-16 font-bold text-gray-900">
                    {section.name}
                  </caption>
                  <thead>
                    <tr>
                      <th className="sr-only" scope="col">
                        Feature
                      </th>
                      <th className="sr-only" scope="col">
                        Included
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {section.features.map((feature, i) => (
                      <tr key={i} className="border-t border-gray-200">
                        <th
                          className="py-20 px-16 text-left text-14-16 font-normal text-gray-700"
                          scope="row"
                        >
                          {feature.name}
                        </th>
                        <td className="py-20 pr-16">
                          {typeof feature.tiers[tier.name] === "string" ? (
                            <span className="block text-right text-14-16 text-gray-700">
                              {feature.tiers[tier.name]}
                            </span>
                          ) : (
                            <>
                              {feature.tiers[tier.name] === true ? (
                                <CheckIcon
                                  className="text-green-500 ml-auto h-20 w-20"
                                  aria-hidden="true"
                                />
                              ) : (
                                <MinusIcon
                                  className="ml-auto h-20 w-20 text-gray-400"
                                  aria-hidden="true"
                                />
                              )}

                              <span className="sr-only">
                                {feature.tiers[tier.name] === true
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}

              <div
                className={classNames(
                  tierIdx < tiers.length - 1 ? "border-b py-20" : "pt-5",
                  "border-t border-gray-200  "
                )}
              >
                <Button
                  href={tier.href}
                  className="rounded-md block w-full border border-gray-800 bg-gray-800 py-2 text-center text-14-16 font-bold text-white hover:bg-gray-900"
                  styling={tier.highlighted ? "solid" : "outline"}
                >
                  {tier.buttonText}
                </Button>
              </div>
            </section>
          ))}
        </div>

        {/* md+ */}
        <div className="hidden md:block">
          <table className="h-px w-full table-fixed">
            <caption className="sr-only">Pricing plan comparison</caption>
            <thead>
              <tr>
                <th
                  className="font-medium px-24 pb-4 text-left text-14-16 text-gray-900"
                  scope="col"
                >
                  <span className="sr-only">Feature by</span>
                </th>
                {tiers.map((tier) => (
                  <th
                    key={tier.name}
                    className="w-1/4 px-24 pb-4 text-left text-18-24 text-gray-900"
                    scope="col"
                  >
                    {tier.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              <tr>
                <th className="" scope="row"></th>
                {tiers.map((tier) => (
                  <td key={tier.name} className="h-full px-24 pb-16 align-top">
                    <div className="relative flex h-full flex-col justify-between gap-20">
                      <div className="mt-4 flex flex-col gap-x-8 md:min-h-[80px] lg:min-h-min lg:flex-row lg:items-end">
                        <span className="pt-20 font-display text-18-24 font-bold sm:text-24-32">
                          {tier.price}
                        </span>
                        {tier.pricePer && (
                          <span className="text-14-20 sm:text-18-28">
                            {tier.pricePer}
                          </span>
                        )}
                      </div>
                      <p className="mt-4 mb-16 min-h-[60px] text-14-20 text-gray-700 md:min-h-[100px] lg:min-h-[60px]">
                        {tier.description}
                      </p>
                      <Button
                        square
                        href={tier.href}
                        styling={tier.highlighted ? "solid" : "outline"}
                      >
                        {tier.buttonText}
                      </Button>
                    </div>
                  </td>
                ))}
              </tr>
              {sections.map((section) => (
                <Fragment key={section.name}>
                  <tr>
                    {section.displayTitle && (
                      <th
                        className="bg-gray-200 py-16 pl-20 text-left text-14-16 text-gray-900"
                        colSpan={4}
                        scope="colgroup"
                      >
                        {section.displayTitle || section.name}
                      </th>
                    )}
                  </tr>
                  {section.features.map((feature, i) => (
                    <tr key={i}>
                      <th
                        className="py-20 px-20 text-left text-14-16 font-normal text-gray-700"
                        scope="row"
                      >
                        <ConditionalWrapper
                          condition={Boolean(feature.tooltip)}
                          wrapper={(children) => (
                            <Tooltip content={feature.tooltip}>
                              <span className="cursor-help border-b border-dashed border-gray-600">
                                {children}
                              </span>
                            </Tooltip>
                          )}
                        >
                          {feature.name}
                        </ConditionalWrapper>
                      </th>
                      {tiers.map((tier) => (
                        <td key={tier.name} className="py-8 px-24">
                          {typeof feature.tiers[tier.name] === "string" ? (
                            <span className="block text-14-16 text-gray-700">
                              {feature.tiers[tier.name]}
                            </span>
                          ) : (
                            <>
                              {feature.tiers[tier.name] === true ? (
                                <CheckIcon
                                  className="text-green-500 h-20 w-20"
                                  aria-hidden="true"
                                />
                              ) : (
                                <MinusIcon
                                  className="h-20 w-20 text-gray-400"
                                  aria-hidden="true"
                                />
                              )}

                              <span className="sr-only">
                                {feature.tiers[tier.name] === true
                                  ? "Included"
                                  : "Not included"}{" "}
                                in {tier.name}
                              </span>
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <th className="sr-only" scope="row">
                  Choose your plan
                </th>
                {tiers.map((tier) => (
                  <td key={tier.name} className="px-20 pt-20">
                    <Button
                      square
                      href={tier.href}
                      styling={tier.highlighted ? "solid" : "outline"}
                    >
                      {tier.buttonText}
                    </Button>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};
